import React from 'react';
import { useTranslation } from 'react-i18next';
import { AnimalPhotosService } from './AnimalPhotosService';
import { AnimalPhotoWithUrl } from '../models/animal-photos';
import { extractApiErrorMessage, notifications } from '../utils';

interface UseAnimalPhotosResult {
  photos: AnimalPhotoWithUrl[];
  totalCount: number;
  isBusy: boolean;
  /** True once a fetch has settled, successfully or not - lets callers tell "no photos" from "not asked yet". */
  loaded: boolean;
  reload: () => void;
}

/**
 * One page of an animal's photos, each with a link that displays it. The list does not carry links,
 * so the page's links are requested together once the list arrives. A photo whose link fails keeps
 * its place with a null `url` rather than failing the page.
 */
export const useAnimalPhotos = (animalId: string, page: number, size: number): UseAnimalPhotosResult => {
  const { t } = useTranslation();
  const [photos, setPhotos] = React.useState<AnimalPhotoWithUrl[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    const service = new AnimalPhotosService();
    // The animal or page can change while a request is in flight; ignore anything but the newest one.
    let cancelled = false;

    setIsBusy(true);

    service
      .getPhotos(animalId, page, size)
      .then(async (result) => {
        const items = result.items ?? [];
        const links = await Promise.allSettled(items.map((photo) => service.getDownloadUrl(animalId, photo.id)));

        if (cancelled) return;

        setPhotos(
          items.map((photo, index) => {
            const link = links[index];
            return { ...photo, url: link.status === 'fulfilled' ? link.value.url : null };
          })
        );
        setTotalCount(result.totalCount ?? 0);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setPhotos([]);
        setTotalCount(0);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setIsBusy(false);
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [animalId, page, size, reloadToken]);

  const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);

  return { photos, totalCount, isBusy, loaded, reload };
};

export default useAnimalPhotos;
